import { createMemo, createSignal, type Accessor } from 'solid-js';
import { sourcesStore } from '../store/sourcesStore';
import { persistantStore } from '../store/persistantStore';
import { selectedSources } from '../store/filterStore';
import {
  ensureValidSourceSelection,
  getAllSourceNames,
  isFleetSourceCatalogReady,
  validateSourceNamesForCatalog,
  waitForSourcesCatalogForContext,
  type InitializeSourceSelectionsOptions,
} from './sourceInitialization';

export { isFleetSourceCatalogReady };

export interface FleetSourcesContextOptions extends InitializeSourceSelectionsOptions {}

const [hydratedSourcesContextKey, setHydratedSourcesContextKey] = createSignal('');

function buildProjectContextKey(className: string, projectId: number): string {
  const cn = String(className ?? '').trim();
  const pid = Number(projectId ?? 0);
  if (!cn || !pid) {
    return '';
  }
  return `${cn.toLowerCase()}:${pid}`;
}

function isSelectedSourcesHydratedForContext(className: string, projectId: number): boolean {
  const contextKey = buildProjectContextKey(className, projectId);
  return contextKey !== '' && hydratedSourcesContextKey() === contextKey;
}

/** Sync: catalog ready and filterStore selectedSources hydrated for class+project. */
export function isFleetSourcesContextReady(
  className?: string,
  projectId?: number
): boolean {
  const cn = String(className ?? persistantStore.selectedClassName() ?? '').trim();
  const pid = Number(projectId ?? persistantStore.selectedProjectId() ?? 0);
  if (!cn || !pid) {
    return false;
  }
  if (!isFleetSourceCatalogReady()) {
    return false;
  }
  return isSelectedSourcesHydratedForContext(cn, pid);
}

/** Wait for catalog + hydrate selectedSources; returns false if context changes or catalog unavailable. */
export async function ensureFleetSourcesContextReady(
  options?: FleetSourcesContextOptions
): Promise<boolean> {
  const className = String(options?.className ?? persistantStore.selectedClassName() ?? '').trim();
  const projectId = Number(options?.projectId ?? persistantStore.selectedProjectId() ?? 0);
  if (!className || !projectId) {
    return false;
  }

  const contextKey = buildProjectContextKey(className, projectId);
  const catalogReady = await waitForSourcesCatalogForContext(className, projectId);
  if (!catalogReady || !isFleetSourceCatalogReady()) {
    return false;
  }
  if (buildProjectContextKey(className, projectId) !== contextKey) {
    return false;
  }

  if (!isSelectedSourcesHydratedForContext(className, projectId)) {
    await ensureValidSourceSelection();
    if (buildProjectContextKey(className, projectId) === contextKey) {
      setHydratedSourcesContextKey(contextKey);
    }
  }

  return isFleetSourcesContextReady(className, projectId);
}

/**
 * Resolve fleet source_names for history API calls.
 * Waits for catalog + hydration; empty filterStore selection means ALL catalog sources.
 */
export async function resolveFleetSourceNamesForFetch(
  options?: FleetSourcesContextOptions
): Promise<string[]> {
  const className = String(options?.className ?? persistantStore.selectedClassName() ?? '').trim();
  const projectId = Number(options?.projectId ?? persistantStore.selectedProjectId() ?? 0);
  if (!className || !projectId) {
    return [];
  }

  const ready = await ensureFleetSourcesContextReady({ className, projectId });
  if (!ready || !isFleetSourceCatalogReady()) {
    return [];
  }

  const catalog = sourcesStore.sources();
  if (catalog.length === 0) {
    return [];
  }

  const selected = selectedSources();
  if (Array.isArray(selected) && selected.length > 0) {
    const validated = validateSourceNamesForCatalog(selected, className, catalog);
    if (validated.length > 0) {
      return validated;
    }
  }

  return getAllSourceNames(catalog);
}

/** Reactive accessor for fleet page render/fetch gates. */
export function createFleetSourcesReadyMemo(): Accessor<boolean> {
  return createMemo(() => {
    sourcesStore.isReady();
    sourcesStore.sources();
    hydratedSourcesContextKey();
    selectedSources();
    const className = persistantStore.selectedClassName();
    const projectId = persistantStore.selectedProjectId();
    return isFleetSourcesContextReady(className, projectId);
  });
}
